import { AxiosResponse } from 'axios';
import { components } from 'generated/payment-initialisation/schema';

import instance from '../Axios/instance';

type RemittersListResponse = components['schemas']['RemittersListResponse'];

interface CacheEntry {
  response: AxiosResponse<RemittersListResponse>;
  timestamp: number;
}

export class RemittersService {
  private static cache: Map<string, CacheEntry> = new Map();

  private static readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  private static readonly MAX_CACHE_SIZE = 50;

  private static getCacheKey(
    accountId: string,
    requiresAction: boolean,
    searchQuery: string,
    pageNumber: number,
  ): string {
    return JSON.stringify({
      accountId,
      requiresAction,
      searchQuery,
      pageNumber,
    });
  }

  private static getCachedEntry(cacheKey: string): CacheEntry | null {
    const entry = this.cache.get(cacheKey);

    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > this.CACHE_TTL_MS;

    if (isExpired) {
      this.cache.delete(cacheKey);
      return null;
    }

    return entry;
  }

  public static invalidateCache(): void {
    this.cache.clear();
  }

  public static async getRemitters(
    accountId: string,
    requiresAction: boolean,
    searchQuery: string,
    pageNumber: number,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<RemittersListResponse>> {
    const cacheKey = this.getCacheKey(accountId, requiresAction, searchQuery, pageNumber);
    const cachedEntry = this.getCachedEntry(cacheKey);

    if (cachedEntry) {
      return cachedEntry.response;
    }

    const config = accountId ? {
      headers: {
        'account-id': accountId,
      },
    } : {};

    const response = await instance.get('/bene/remitters', {
      ...config,
      signal,
      params: {
        incompleteOnly: requiresAction, pageNumber: pageNumber + 1, pageSize: 10, searchText: searchQuery.trim(),
      },
    });

    // Don't cache if the request was aborted
    if (signal?.aborted) {
      return response;
    }

    // Evict oldest entry if cache is full
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(cacheKey, {
      response,
      timestamp: Date.now(),
    });

    return response;
  }

  public static async createRemitter(remitter: components['schemas']['RemitterCreateRequest'])
  : Promise<AxiosResponse<components['schemas']['RemitterResponse']>> {
    const response = await instance.post('/bene/remitters', remitter);
    this.invalidateCache();
    return response;
  }

  public static async updateRemitter(remitterId: string,
    remitter: components['schemas']['RemitterUpdateRequest']): Promise<AxiosResponse<components['schemas']['RemitterResponse']>> {
    const response = await instance.put(`/bene/remitters/${remitterId}`, remitter);
    this.invalidateCache();
    return response;
  }
}

export default RemittersService;
